/**
 * Navigation Context
 * 
 * Provides centralized navigation state for dynamic breadcrumb labels.
 * Pages can register human-readable labels for URL segments (UUIDs/slugs).
 * 
 * @module contexts/NavigationContext
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

interface NavigationContextType {
  /** Map of URL segment → human-readable label */
  labels: Record<string, string>;
  /** Register a label for a URL segment */
  setLabel: (segment: string, label: string) => void;
  /** Clear a label for a URL segment */
  clearLabel: (segment: string) => void;
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

export function NavigationProvider({ children }: { children: React.ReactNode }) {
  const [labels, setLabels] = useState<Record<string, string>>({});

  const setLabel = useCallback((segment: string, label: string) => {
    if (!segment || !label) return;
    setLabels(prev => {
      // Only update if different to avoid unnecessary re-renders
      if (prev[segment] === label) return prev;
      return { ...prev, [segment]: label };
    });
  }, []);

  const clearLabel = useCallback((segment: string) => {
    if (!segment) return;
    setLabels(prev => {
      if (!(segment in prev)) return prev;
      const next = { ...prev };
      delete next[segment];
      return next;
    });
  }, []);

  const value = useMemo(() => ({ labels, setLabel, clearLabel }), [labels, setLabel, clearLabel]);

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useNavigation() {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within NavigationProvider');
  }
  return context;
}
